import Observable from 'zen-observable';

import * as Model from '../model/types';
import * as ID from '../utils/id';

import {Server} from '../server/types';
import {Client} from './types';

interface Listener<T extends Model.Type = Model.Type> {
  id: string;
  done: boolean;
  observer: ZenObservable.SubscriptionObserver<T>;
  node: Model.Node<T>;
}

export class BasicClient implements Client {
  private listeners = new Map<string, Listener>();
  private loadingListeners = new Map<
    string,
    ZenObservable.SubscriptionObserver<boolean>
  >();
  private loading = new Observable<boolean>(observer => {
    const id = ID.ID();
    this.loadingListeners.set(id, observer);

    return () => this.loadingListeners.delete(id);
  });

  public constructor(private readonly server: Server) {
    setImmediate(this.loopIteration.bind(this));
  }

  public subscribe<T extends Model.Type>(node: Model.Node<T>): Observable<any> {
    this.setIsLoading(true);
    return new Observable(observer => {
      const listenerId = ID.ID();
      this.listeners.set(listenerId, {
        id: listenerId,
        done: false,
        observer,
        node,
      });
      return () => {
        this.listeners.delete(listenerId);
      };
    });
  }

  // I'm adding this for now to make it easier to switch
  // refineNode to use client. But we should really make
  // refineNode() also subscribable, meaning we want to
  // wire observers through that code path.
  // TODO: really, fix this. It would mean we can poll correctly
  // and efficiently for both useNodeValue() and useNodeWithServerType()
  public query<T extends Model.Type>(node: Model.Node<T>): Promise<any> {
    const obs = this.subscribe(node);
    return new Promise((resolve, reject) => {
      const sub = obs.subscribe(
        nodeRes => {
          resolve(nodeRes);
          sub.unsubscribe();
        },
        caughtError => {
          reject(caughtError);
          sub.unsubscribe();
        }
      );
    });
  }

  public loadingObservable() {
    return this.loading;
  }

  private setIsLoading(loading: boolean) {
    this.loadingListeners.forEach(observer => observer.next(loading));
  }

  private async loopIteration() {
    const notDoneListeners = Array.from(this.listeners.values()).filter(
      l => !l.done
    );
    this.listeners.forEach(l => (l.done = true));
    if (notDoneListeners.length > 0) {
      let error: any;
      let results: any[] = [];
      // console.time('graph execute');
      try {
        results = await this.executeForwardListeners(notDoneListeners);
      } catch (e) {
        error = e;
        // TODO(np): Do we need to do anything to recover here?
      }
      // console.timeEnd('graph execute');

      // notify listeners
      // console.time('graph notify');
      if (error != null) {
        for (const listener of notDoneListeners) {
          if (this.listeners.get(listener.id) != null) {
            const observer = listener.observer;
            observer.error(error);
          }
        }
      } else {
        for (let i = 0; i < notDoneListeners.length; i++) {
          const listener = notDoneListeners[i];
          // Make sure the listener hasn't unsubscribed
          if (this.listeners.get(listener.id) != null) {
            const observer = listener.observer;
            observer.next(results[i]);
          } else {
            console.warn('MISSING LISTENER', listener.id);
          }
        }
      }
      // console.timeEnd('graph notify');
    }

    if (!Array.from(this.listeners.values()).some(listener => !listener.done)) {
      this.setIsLoading(false);
    }

    setTimeout(this.loopIteration.bind(this), 50);
  }

  private async executeForwardListeners(listeners: Listener[]) {
    const targetNodes = listeners.map(l => l.node);
    return this.server.query(targetNodes);
  }
}
